import { Component, DoCheck, HostListener, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GuideService } from './guide.service';
import { SpectrumGuidePanelComponent } from './panel/guide-panel.component';
import { SpectrumGuideTourComponent } from './tour/guide-tour.component';

/** Wider than this, the open Guide pushes the app aside instead of covering it. */
const PUSH_FROM_PX = 1100;

/**
 * The Guide, mounted once in an app's shell: the side panel, the tour, and
 * the first-visit offer of a tour. It follows the router so the panel is
 * always about the screen on show, and listens for `?` and F1.
 *
 * Open on a wide screen, it pushes the whole app left (a class on <body>)
 * so somebody can read a step and click the screen at the same time; on a
 * narrow one it covers the screen like a drawer.
 */
@Component({
    selector: 'spectrum-guide',
    imports: [SpectrumGuidePanelComponent, SpectrumGuideTourComponent],
    // The push is a rule on <body>, which no component's own styles can reach.
    encapsulation: ViewEncapsulation.None,
    template: `
        @if (guide.enabled) {
            <spectrum-guide-panel />
            <spectrum-guide-tour />
            @if (guide.nudge; as nudge) {
                <div class="sg-nudge" role="dialog" aria-label="Tour offer">
                    <span class="sg-nudge-face"><i class="fas fa-lightbulb"></i></span>
                    <div>
                        @if (nudge.welcome) {
                            <b>Welcome to the Spectrum Guide</b>
                            <p>Can I show you around? It takes about a minute: where the menu is, how to switch module or school, and where I am when you need help.</p>
                        } @else {
                            <b>First time on {{ nudge.ref.topic.title }}?</b>
                            <p>I can walk you round it in about a minute. You can stop at any point.</p>
                        }
                        <div class="sg-nudge-row">
                            <button type="button" class="sg-nudge-yes" (click)="guide.startTour(nudge.ref)">Show me around</button>
                            <button type="button" class="sg-nudge-no" (click)="guide.dismissNudge()">Not now</button>
                        </div>
                        <small>You can always start it from the Guide (press ?).</small>
                    </div>
                </div>
            }
        }
    `,
    styles: [`
        spectrum-guide { display: contents; }
        @media (min-width: ${PUSH_FROM_PX}px) {
            body.spectrum-guide-open { padding-right: var(--spectrum-guide-w, 400px); }
            /* The shell (the Guide's siblings) becomes the box its fixed drawers,
               dialogs and toasts are placed in, so they open beside the Guide
               rather than underneath it. */
            body.spectrum-guide-open.spectrum-guide-contain :has(> spectrum-guide) > :not(spectrum-guide) { transform: translateZ(0); }
        }
        body.spectrum-guide-animate { transition: padding-right .22s ease; }
        .sg-nudge {
            position: fixed; right: 20px; bottom: 20px; z-index: 1080;
            width: 340px; max-width: calc(100vw - 32px);
            display: flex; gap: 12px; box-sizing: border-box;
            padding: 14px 16px; border-radius: 14px;
            background: #0f172a; color: #cbd5e1;
            box-shadow: 0 18px 40px rgba(15, 23, 42, .3);
            animation: sg-nudge-rise .35s ease;
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            font-size: 13px; line-height: 1.5; text-align: left;
        }
        @keyframes sg-nudge-rise { from { transform: translateY(12px); opacity: 0; } }
        .sg-nudge-face { width: 34px; height: 34px; flex: none; border-radius: 10px; display: grid; place-items: center; background: #fcd34d; color: #451a03; }
        .sg-nudge b { color: #fff; font-size: 14px; }
        .sg-nudge p { margin: 4px 0 10px; }
        .sg-nudge small { display: block; margin-top: 8px; color: #64748b; font-size: 11.5px; }
        .sg-nudge-row { display: flex; gap: 10px; align-items: center; }
        .sg-nudge-yes { border: 0; border-radius: 8px; padding: 7px 12px; background: #fcd34d; color: #451a03; font: inherit; font-weight: 800; cursor: pointer; }
        .sg-nudge-no { border: 0; background: none; color: #94a3b8; font: inherit; font-weight: 600; cursor: pointer; }
        @media (prefers-reduced-motion: reduce) {
            .sg-nudge { animation: none; }
            body.spectrum-guide-animate { transition: none; }
        }
    `],
})
export class SpectrumGuideComponent implements OnInit, DoCheck, OnDestroy {
    readonly guide = inject(GuideService);

    /**
     * Whether the shell's fixed drawers and dialogs are kept beside the open
     * Guide. Right for a shell that is one screen tall and scrolls inside;
     * a shell whose page scrolls as a whole (System Admin) passes false, since
     * its fixed sidebar would otherwise scroll with the page while the Guide
     * is open.
     */
    @Input() containFixed = true;
    private router = inject(Router);
    private subs = new Subscription();
    private wasOpen = false;

    ngOnInit(): void {
        if (!this.guide.enabled) {
            return;
        }
        document.body.classList.add('spectrum-guide-animate');
        document.body.classList.toggle('spectrum-guide-contain', this.containFixed);
        this.guide.setScreen(this.router.url);
        this.subs.add(this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => this.guide.setScreen(event.urlAfterRedirects)));
    }

    /** Keeps <body>'s class in step with the panel, however it was opened. */
    ngDoCheck(): void {
        if (this.guide.open !== this.wasOpen) {
            this.wasOpen = this.guide.open;
            document.body.classList.toggle('spectrum-guide-open', this.guide.open);
            // Screens that size themselves to the window (charts, maps) hear about the new width.
            setTimeout(() => window.dispatchEvent(new Event('resize')), 260);
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        document.body.classList.remove('spectrum-guide-open', 'spectrum-guide-animate', 'spectrum-guide-contain');
    }

    @HostListener('document:keydown', ['$event'])
    onKey(event: KeyboardEvent): void {
        if (!this.guide.enabled || this.guide.tour || event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }
        if (event.key === 'F1') {
            event.preventDefault();
            this.guide.toggle();
        } else if (event.key === '?' && !isTyping(event.target)) {
            event.preventDefault();
            this.guide.toggle();
        } else if (event.key === 'Escape' && this.guide.open && !isTyping(event.target)) {
            this.guide.close();
        }
    }
}

function isTyping(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) {
        return false;
    }
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}
